import type { ReactNode } from 'react'
import { Tooltip } from '@/components/Tooltip'
import { IconButton } from '@/components/IconButton'

import styles from './ChatPopover.module.css'

export interface ChatPopoverProps {
  title: string
  onClose: () => void
  headerActions: ReactNode
  children: ReactNode
}

/**
 * Chat floats over the job instead of replacing it, so a pro can answer the
 * client and still see the documents they are talking about.
 */
export function ChatPopover({
  title,
  onClose,
  headerActions,
  children,
}: ChatPopoverProps) {
  return (
    <aside
      className={styles.root}
      aria-label={`Chat with ${title}`}
      data-test="job-chat-popover"
    >
      <div className={styles.header}>
        <span className={styles.title}>Chat · {title}</span>
        {headerActions}
        <IconButton
          name="close"
          label="Close chat"
          variant="ghost"
          onPress={onClose}
          size={16}
        />
      </div>
      <div className={styles.body}>{children}</div>
    </aside>
  )
}

export interface ChatPopoverHeaderActionsProps {
  canJoinCall: boolean
  isJoinCallPending: boolean
  onJoinCall: () => void
  hasConversation: boolean
  isMuted: boolean
  onToggleMute: () => void
  canDownloadConversation: boolean
  onDownloadConversation: () => void
}

export function ChatPopoverHeaderActions({
  canJoinCall,
  isJoinCallPending,
  onJoinCall,
  hasConversation,
  isMuted,
  onToggleMute,
  canDownloadConversation,
  onDownloadConversation,
}: ChatPopoverHeaderActionsProps) {
  return (
    <>
      {canJoinCall && (
        <Tooltip content="Join call" placement="bottom">
          <IconButton
            name="phone"
            label="Join call"
            variant="ghost"
            isDisabled={isJoinCallPending}
            onPress={onJoinCall}
            size={16}
          />
        </Tooltip>
      )}
      {hasConversation && (
        <Tooltip
          content={isMuted ? 'Unmute conversation' : 'Mute conversation'}
          placement="bottom"
        >
          <IconButton
            name={isMuted ? 'notifications-thick-muted' : 'notifications-thick'}
            label={isMuted ? 'Unmute conversation' : 'Mute conversation'}
            variant="ghost"
            aria-pressed={isMuted}
            onPress={onToggleMute}
            size={16}
          />
        </Tooltip>
      )}
      {hasConversation && canDownloadConversation && (
        <Tooltip content="Download conversation history" placement="bottom">
          <IconButton
            name="download"
            label="Download conversation history"
            variant="ghost"
            onPress={onDownloadConversation}
            testId="download-conversation-new"
            size={16}
          />
        </Tooltip>
      )}
    </>
  )
}
